import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'products',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'productsApi',
  baseQuery,
  tagTypes: ['products', 'categories', 'tags'],
  endpoints: builder => ({
    // Products Slices
    getProducts: builder.query({
      query: params => ({
        url: endpoints.PRODUCTS + '/list',
        method: 'GET',
        params,
      }),
      providesTags: ['products'],
    }),
    getProductDetails: builder.query({
      query: id => ({
        url: `${endpoints.PRODUCTS}/details/${id}`,
        method: 'GET',
      }),
      providesTags: ['products'],
    }),
    deleteProduct: builder.mutation({
      query: id => ({
        url: `${endpoints.PRODUCTS}/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['products'],
    }),
    addProducts: builder.mutation({
      query: data => ({
        url: endpoints.PRODUCTS + '/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),
    updateProduct: builder.mutation({
      query: data => ({
        url: endpoints.PRODUCTS + '/update/' + data.product_id,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),

    // Product Attributes type slices
    getProductsAttributeType: builder.query({
      query: () => ({
        url: `${endpoints.PRODUCTS}/attributes/list`,
        method: 'GET',
      }),
      providesTags: ['products'],
    }),

    getSingleProductsAttributeType: builder.query({
      query: id => ({
        url: `${endpoints.PRODUCTS}/attributes/list/values/${id}`,
        method: 'GET',
      }),
      providesTags: ['products'],
    }),

    addProductsAttributeType: builder.mutation({
      query: data => ({
        url: `${endpoints.PRODUCTS}/attributes/add`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),

    updateProductsAttributeType: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.PRODUCTS}/attributes/update`,
        method: 'PUT',
        body: { ...data, id },
      }),
      invalidatesTags: ['products'],
    }),

    updateAttributeDisplayOrder: builder.mutation({
      query: ({ id, display_order }) => ({
        url: `${endpoints.PRODUCTS}/attributes/update-display-order`,
        method: 'PUT',
        body: { id, display_order },
      }),
      invalidatesTags: ['products'],
    }),

    updateMultipleAttributeDisplayOrder: builder.mutation({
      query: ({ attributes }) => ({
        url: `${endpoints.PRODUCTS}/attributes/update-multiple-display-order`,
        method: 'PUT',
        body: { attributes },
      }),
      invalidatesTags: ['products'],
    }),

    deleteProductsAttributeType: builder.mutation({
      query: id => ({
        url: `${endpoints.PRODUCTS}/attributes/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['products'],
    }),

    // Product Attributes Values slices
    addProductsAttributeValues: builder.mutation({
      query: ({ data, id }) => ({
        url: `${endpoints.PRODUCTS}/attributes/values/add`,
        method: 'POST',
        body: { ...data, id },
      }),
      invalidatesTags: ['products'],
    }),

    updateProductsAttributeValue: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.PRODUCTS}/attributes/values/update`,
        method: 'PUT',
        body: { ...data, id },
      }),
      invalidatesTags: ['products'],
    }),

    deleteProductsAttributeValue: builder.mutation({
      query: id => ({
        url: `${endpoints.PRODUCTS}/attributes/values/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['products'],
    }),

    // Product Categories slices - Updated for infinite level support
    getProductsCategories: builder.query({
      query: params => ({
        url: endpoints.PRODUCTS_CATEGORIES_LIST,
        method: 'GET',
        params,
      }),
      providesTags: ['categories'],
    }),

    getProductsCategoriesTree: builder.query({
      query: params => ({
        url: endpoints.PRODUCTS_CATEGORIES_TREE,
        method: 'GET',
        params,
      }),
      providesTags: ['categories'],
    }),

    getProductsCategoryPath: builder.query({
      query: categoryId => ({
        url: `${endpoints.PRODUCTS_CATEGORIES_PATH}/${categoryId}`,
        method: 'GET',
      }),
      providesTags: ['categories'],
    }),

    getProductsSubcategories: builder.query({
      query: categoryId => ({
        url: `${endpoints.PRODUCTS_CATEGORIES}/${categoryId}`,
        method: 'GET',
      }),
      providesTags: ['categories'],
    }),

    addProductsCategories: builder.mutation({
      query: data => ({
        url: endpoints.PRODUCTS_CATEGORIES_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['categories'],
    }),

    updateProductsCategories: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.PRODUCTS_CATEGORIES_UPDATE}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['categories'],
    }),

    moveProductsCategory: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.PRODUCTS_CATEGORIES_MOVE}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['categories'],
    }),

    deleteProductsCategories: builder.mutation({
      query: id => ({
        url: `${endpoints.PRODUCTS_CATEGORIES_DELETE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['categories'],
    }),

    rebuildNestedSet: builder.mutation({
      query: () => ({
        url: endpoints.PRODUCTS_CATEGORIES_REBUILD,
        method: 'POST',
      }),
      invalidatesTags: ['categories'],
    }),

    // Product Tags slices
    getProductsTags: builder.query({
      query: params => ({
        url: endpoints.PRODUCTS_TAGS + '/list',
        method: 'GET',
        params,
      }),
      providesTags: ['tags'],
    }),

    addProductsTags: builder.mutation({
      query: data => ({
        url: endpoints.PRODUCTS_TAGS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['tags'],
    }),

    deleteProductsTags: builder.mutation({
      query: id => ({
        url: `${endpoints.PRODUCTS_TAGS}/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['tags'],
    }),

    updateProductTags: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.PRODUCTS_TAGS}/update/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['tags'],
    }),

    // Product Variants slices
    getProductVariants: builder.query({
      query: productId => {
        if (!productId) {
          throw new Error('Product ID is required');
        }
        return {
          url: `${endpoints.PRODUCTS}/${productId}/variants`,
          method: 'GET',
        };
      },
      providesTags: ['products'],
    }),

    getVariantDetails: builder.query({
      query: variantId => ({
        url: `${endpoints.PRODUCTS_VARIANTS}/${variantId}`,
        method: 'GET',
      }),
      providesTags: ['products'],
    }),

    addProductVariant: builder.mutation({
      query: data => ({
        url: `${endpoints.PRODUCTS_VARIANTS}/create`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),

    updateProductVariant: builder.mutation({
      query: ({ variantId, ...data }) => ({
        url: `${endpoints.PRODUCTS_VARIANTS}/${variantId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),

    deleteProductVariant: builder.mutation({
      query: variantId => ({
        url: `${endpoints.PRODUCTS_VARIANTS}/${variantId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['products'],
    }),

    bulkCreateProductVariants: builder.mutation({
      query: data => ({
        url: `${endpoints.PRODUCTS_VARIANTS}/bulk-create`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['products'],
    }),
  }),
});

export const { actions: ProductsActions } = slice;

export const useProductsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: ProductsActions,
    useGetProductsQuery: api.useLazyGetProductsQuery,
    useGetProductDetailsQuery: api.useLazyGetProductDetailsQuery,
    useLazyGetProductsAttributeTypeQuery:
      api.useLazyGetProductsAttributeTypeQuery,
    useAddProductsAttributeTypeMutation:
      api.useAddProductsAttributeTypeMutation,
    useLazyGetProductsCategoriesQuery: api.useLazyGetProductsCategoriesQuery,
    useLazyGetProductsCategoriesTreeQuery: api.useLazyGetProductsCategoriesTreeQuery,
    useLazyGetProductsCategoryPathQuery: api.useLazyGetProductsCategoryPathQuery,
    useLazyGetProductsSubcategoriesQuery: api.useLazyGetProductsSubcategoriesQuery,
    useLazyGetProductsTagsQuery: api.useLazyGetProductsTagsQuery,

    useLazyGetSingleProductsAttributeTypeQuery:
      api.useLazyGetSingleProductsAttributeTypeQuery,

    useAddProductsCategoriesMutation: api.useAddProductsCategoriesMutation,
    useAddProductsTagsMutation: api.useAddProductsTagsMutation,
    useUpdateProductTagsMutation: api.useUpdateProductTagsMutation,
    useAddProductsAttributeValuesMutation:
      api.useAddProductsAttributeValuesMutation,

    useAddProductsMutation: api.useAddProductsMutation,

    useUpdateProductsAttributeTypeMutation:
      api.useUpdateProductsAttributeTypeMutation,

    useUpdateAttributeDisplayOrderMutation:
      api.useUpdateAttributeDisplayOrderMutation,

    useUpdateMultipleAttributeDisplayOrderMutation:
      api.useUpdateMultipleAttributeDisplayOrderMutation,

    useUpdateProductsAttributeValueMutation:
      api.useUpdateProductsAttributeValueMutation,

    useUpdateProductsCategoriesMutation:
      api.useUpdateProductsCategoriesMutation,

    useMoveProductsCategoryMutation: api.useMoveProductsCategoryMutation,
    useRebuildNestedSetMutation: api.useRebuildNestedSetMutation,

    useDeleteProductsAttributeTypeMutation:
      api.useDeleteProductsAttributeTypeMutation,
    useDeleteProductsTagsMutation: api.useDeleteProductsTagsMutation,

    useDeleteProductsAttributeValueMutation:
      api.useDeleteProductsAttributeValueMutation,

    useDeleteProductsCategoriesMutation:
      api.useDeleteProductsCategoriesMutation,
    useDeleteProductMutation: api.useDeleteProductMutation,
    useUpdateProductsMutation: api.useUpdateProductMutation,

    // Product Variants hooks
    useGetProductVariantsQuery: api.useLazyGetProductVariantsQuery,
    useGetVariantDetailsQuery: api.useLazyGetVariantDetailsQuery,
    useAddProductVariantMutation: api.useAddProductVariantMutation,
    useUpdateProductVariantMutation: api.useUpdateProductVariantMutation,
    useDeleteProductVariantMutation: api.useDeleteProductVariantMutation,
    useBulkCreateProductVariantsMutation:
      api.useBulkCreateProductVariantsMutation,
  };
};
